import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CookiesService } from './cookies.service';
import { isJwtExpiring } from './jwt-token.util';

interface AccessTokenResponse {
  accessToken: string;
}

export class SessionTokenError extends Error {
  readonly status = 401;
  readonly error = { message: 'Session token is missing or invalid' };

  constructor(message: string) {
    super(message);
    this.name = 'SessionTokenError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class SessionTokenService {
  private readonly REFRESH_BUFFER_SECONDS = 120;
  private readonly REFRESH_API = `${environment.backendUrl}auth/refresh`;
  private readonly SIGN_OUT_API = `${environment.backendUrl}auth/sign-out`;

  private accessToken: string | null = null;
  private refreshInProgress: Promise<string> | null = null;

  constructor(
    private http: HttpClient,
    private cookiesService: CookiesService
  ) {
    this.removeLegacyTokenStorage();
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setAccessToken(accessToken: string): void {
    this.accessToken = accessToken;
  }

  clearTokens(): void {
    this.accessToken = null;
    this.removeLegacyTokenStorage();
  }

  async ensureValidAccessToken(forceRefresh = false): Promise<string> {
    if (
      !forceRefresh &&
      this.accessToken &&
      !isJwtExpiring(this.accessToken, this.REFRESH_BUFFER_SECONDS)
    ) {
      return this.accessToken;
    }

    return this.refreshAccessToken();
  }

  async restoreSession(): Promise<string> {
    return this.ensureValidAccessToken(true);
  }

  async signOut(): Promise<void> {
    try {
      await this.http
        .post<void>(this.SIGN_OUT_API, {}, { withCredentials: true })
        .toPromise();
    } finally {
      this.clearTokens();
    }
  }

  private async refreshAccessToken(): Promise<string> {
    if (this.refreshInProgress) return this.refreshInProgress;

    this.refreshInProgress = this.requestAccessToken();

    try {
      return await this.refreshInProgress;
    } finally {
      this.refreshInProgress = null;
    }
  }

  private async requestAccessToken(): Promise<string> {
    try {
      const response = await this.http
        .post<AccessTokenResponse>(
          this.REFRESH_API,
          {},
          { withCredentials: true }
        )
        .toPromise();

      if (!response?.accessToken) {
        throw new SessionTokenError(
          'Access token was not returned by the backend'
        );
      }

      this.setAccessToken(response.accessToken);
      return response.accessToken;
    } catch (error) {
      this.accessToken = null;
      throw error;
    }
  }

  private removeLegacyTokenStorage(): void {
    this.cookiesService.deleteCookie('token');
    this.cookiesService.deleteCookie('refreshToken');
    window.localStorage.removeItem('token');
    window.sessionStorage.removeItem('token');
  }
}
